import { Injectable, NgZone } from '@angular/core';
import { ICoreDependencies } from "@core/core-boot/core-boot.types";
import { Core } from "@core/core";
import { CoreBase } from "@core/base/core-base";
import { CoreOffice } from "@core/office/core-office";
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { ClipboardService } from "@services/clipboard/clipboard.service";
import { AiModelsService } from "@services/ai-models/ai-models.service";
import { QuickActionsService } from "@services/quick-actions/quick-actions.service";
import { ThreadAttachmentStore } from "@services/r2-file/thread-attachment.store";
import { TranslateService } from "@ngx-translate/core";
import { CoreEnv } from "@core/core-env/core-env";
import { UserSettingsApiService } from "@services/user-settings-api/user-settings-api.service";
import { SessionCryptoService } from '@services/session-crypto/session-crypto.service';
import { InstructionsService } from '@services/instructions/instructions.service';
import { environment } from "@env";

export const OFFICE_API_VERSION = {
    v1: 1,
    v2: 2,
} as const;

@Injectable({
    providedIn: 'root'
})
export class CoreBoot
{
    constructor(
        private http: HttpClient,
        private router: Router,
        private routerInfo: ActivatedRoute,
        private db: NgxIndexedDBService,
        private clipboard: ClipboardService,
        private core: Core,
        private aiModels: AiModelsService,
        private quickActionsService: QuickActionsService,
        private attachmentStore: ThreadAttachmentStore,
        private translate: TranslateService,
        private userSettingsApi: UserSettingsApiService,
        private sessionCrypto: SessionCryptoService,
        private instructions: InstructionsService,
        private zone: NgZone,
    ) { }

    public async bootstrap(): Promise<void>
    {
        // The Office auth dialog (/auth/msal-dialog) only needs the MSAL redirect
        // flow in MsalDialogComponent — not Office/Core. Skip the heavy init so this
        // APP_INITIALIZER resolves and the router can render the dialog route:
        // booting CoreOffice inside the dialog hangs on some hosts (e.g. Word on
        // macOS) and would block the router — and thus the dialog — entirely.
        if (window.location.pathname.startsWith(environment.msal_dialog_path))
        {
            return;
        }

        const env = await CoreEnv.init();
        const coreNodules = await this.bootstrapCoreModules(env);
        this.core.defineModules(coreNodules, env);
    }

    private async bootstrapCoreModules(coreEnv: CoreEnv): Promise<CoreBase>
    {
        const env = coreEnv.get();
        const dependencies = this.coreDependencies();

        if (env.office === OFFICE_API_VERSION.v1 || env.office === OFFICE_API_VERSION.v2)
        {
            return await CoreOffice.bootstrap(dependencies);
        }

        throw new Error('Implementation for core not found');
    }

    private coreDependencies(): ICoreDependencies
    {
        return {
            http: this.http,
            router: this.router,
            routerInfo: this.routerInfo,
            db: this.db,
            clipboard: this.clipboard,
            aiModels: this.aiModels,
            quickActions: this.quickActionsService,
            attachmentStore: this.attachmentStore,
            translate: this.translate,
            userSettingsApi: this.userSettingsApi,
            sessionCrypto: this.sessionCrypto,
            instructions: this.instructions,
            zone: this.zone,
        };
    }
}
